import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
  GridRowId,
  GridRowModel,
} from "@mui/x-data-grid";
import { memo, useCallback, useMemo, useState } from "react";
import {
  SupplierTableItemFragment,
  useSupplierUpsertMutation,
} from "../../../__generated__/types-and-hooks";
import { useTeam } from "../TeamLayout";
import { useTranslation } from "../../../i18n";
import { useNotify } from "../../../hooks/useNotify";
import EditArrayCell from "../../../components/datagrid/EditArrayCell";
import ArrayCell from "../../../components/datagrid/ArrayCell";
import { Box, Chip, IconButton, Tooltip } from "@mui/joy";
import ContactTooltip from "./ContactTooltip";
import PersonIcon from "@mui/icons-material/Person";
import ModeEditIcon from "@mui/icons-material/ModeEdit";
import SupplierRef from "../../../components/SupplierRef";
import ContactsModal from "./ContactsModal";
import GridStyles from "../../../components/datagrid/GridStyles";

export type TableViewSupplier = any;

export interface TableViewProps {
  suppliers: TableViewSupplier[];
  onUpdate?: (supplierId: string, supplier: any) => void;
  loading?: boolean;
}

const ContactRenderCell = memo(function ({
  onEditContactsClick,
  ...params
}: { onEditContactsClick: (row: any) => void } & GridRenderCellParams) {
  return (
    <Box sx={{ display: "flex", p: 0 }}>
      <Box gap={0.1} sx={{ display: "flex", overflow: "hidden", flex: 1 }}>
        {params.row.contacts.map((contact: any) => (
          <Tooltip
            enterDelay={700}
            leaveDelay={200}
            key={contact.uniqueId}
            variant="outlined"
            arrow
            title={
              <ContactTooltip uniqueId={contact.uniqueId} name={contact.name} />
            }
          >
            <Chip
              variant="soft"
              sx={{ borderRadius: 4, fontSize: 12 }}
              startDecorator={<PersonIcon style={{ fontSize: 12 }} />}
            >
              {contact.name}
            </Chip>
          </Tooltip>
        ))}
      </Box>
      <IconButton
        sx={{ fontSize: 10, p: 0, m: 0 }}
        size="sm"
        color="primary"
        variant="plain"
        onClick={() => onEditContactsClick(params.row)}
      >
        <ModeEditIcon style={{ fontSize: 14 }} />
      </IconButton>
    </Box>
  );
});

export default function TableView({
  suppliers,
  onUpdate,
  loading,
}: TableViewProps) {
  const t = useTranslation();
  const teamCtx = useTeam();
  const team = teamCtx.team;
  const notify = useNotify();

  function RefRenderCell({
    teamSlug,
    ...params
  }: { teamSlug: string } & GridRenderCellParams<any, string>) {
    return (
      <SupplierRef supplierRef={params.row.uniqueId} teamSlug={teamSlug} />
    );
  }

  const [contactsModalOpen, setContactsModalOpen] =
    useState<SupplierTableItemFragment | null>(null);

  function onEditContactsClick(row: SupplierTableItemFragment) {
    setContactsModalOpen(row);
  }

  const [supplierUpsertMutation] = useSupplierUpsertMutation({});
  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: "uniqueId",
        headerName: t("ID"),
        width: 80,
        editable: false,
        renderCell: (params) => (
          <RefRenderCell {...params} teamSlug={team.slug} />
        ),
      },
      {
        field: "name",
        headerName: t("Name"),
        width: 200,
        editable: true,
      },
      {
        field: "countryCode",
        headerName: t("Country"),
        width: 80,
        editable: true,
      },
      {
        field: "city",
        headerName: t("City"),
        width: 100,
        editable: true,
      },
      {
        field: "postalCode",
        headerName: t("Postal Code"),
        width: 100,
        editable: true,
      },
      {
        field: "addressLine1",
        headerName: t("Address Line 1"),
        width: 200,
        editable: true,
      },
      {
        field: "contacts",
        headerName: t("Contacts"),
        width: 300,
        editable: false,
        renderCell: (params) => (
          <ContactRenderCell
            {...params}
            onEditContactsClick={onEditContactsClick}
          />
        ),
      },
      {
        field: "capabilities",
        headerName: t("Capabilities"),
        width: 140,
        editable: true,
        renderCell: (params) => <ArrayCell {...params} />,
        renderEditCell: (params) => <EditArrayCell {...params} />,
      },
    ],
    [t, team.slug]
  );

  const handleProcessRowUpdate = useCallback(
    async (
      newRow: GridRowModel,
      oldRow: GridRowModel,
      { rowId }: { rowId: GridRowId }
    ) => {
      const delta = (field: string, key?: string) =>
        newRow[field] !== oldRow[field]
          ? typeof key === "string" && typeof newRow[field] === "object"
            ? newRow[field][key]
            : newRow[field]
          : void 0;

      const updates = {
        teamId: teamCtx.team.id,
        id: rowId as number,
        name: delta("name"),
        addressLine1: delta("addressLine1"),
        addressLine2: delta("addressLine2"),
        city: delta("city"),
        state: delta("state"),
        postalCode: delta("postalCode"),
        countryCode: delta("countryCode"),
        capabilities: newRow.capabilities ?? oldRow.capabilities,
      };

      console.info("delta", updates);

      const res = await supplierUpsertMutation({
        variables: {
          supplier: updates,
        },
      });
      if (res.errors) {
        throw new Error(res.errors.map((e) => e.message).join("\n"));
      }
      notify({
        type: "success",
        msg: "Updated successfully",
      });
      return newRow;
    },
    [supplierUpsertMutation, teamCtx, notify]
  );

  return (
    <>
      <DataGrid
        {...GridStyles}
        getRowId={(row) => row.uniqueId}
        rows={suppliers}
        loading={loading}
        columns={columns}
        editMode="cell"
        processRowUpdate={handleProcessRowUpdate}
        onProcessRowUpdateError={(err) => {
          notify({
            type: "danger",
            msg: err.message,
          });
        }}
      />
      {contactsModalOpen && (
        <ContactsModal
          supplier={contactsModalOpen}
          onSave={(supplier) => {
            if (onUpdate) {
              onUpdate(supplier.uniqueId, supplier);
            }
          }}
          onClose={() => {
            setContactsModalOpen(null);
          }}
        />
      )}
    </>
  );
}
